/// <reference path="_references.ts"/>

module App.Chat {
	import ToastService = App.Utils.ToastService;

	interface LocalScope extends ng.IScope {
		menu: App.User.UserMenuController;
	}

	export class ChatController {
		private showChat: boolean;
		private chatListener: () => void;

		toastLocation: string = 'bottom left';
		chatPanel: HTMLElement;
		chatInput: HTMLElement;
		REFRESH_USERS_DELAY: number = 150000;
		RETRY_DELAY: number = 3000;
		MAX_RETRIES: number = 5;
		ENTER_KEYCODE: number = 13;
		DEFAULT_MESSAGES_RETRIEVED: number = 50;
		retries: number = 0;

		currentChat: ChatUser = {id: null, name: '', unread: 0, online: false, role: ''};
		subscription: Subscription = null;
		users: ChatUser[] = null;
		totalUnread: number = 0;
		messageLogs: {id: number, log: Message[]}[] = [];
		currentMessageLog: {id: number, log: Message[]} = {
			id: null,
			log: []
		};
		sending: boolean = false;
		drafts: Message[] = [];
		currentDraft: Message = {
			recipient: null,
			message: ''
		};
		getUserInterval: ng.IPromise<any> = null;
		usersOpen: boolean = true;
		usersLoaded: boolean = false;
		search: string = '';
		searchRegExp: RegExp = null;
		lastMessageLength: number = null;
		loadingOld: boolean = false;
		loading: boolean = true;

		constructor(
			private $document: ng.IDocumentService,
			private $interval: ng.IIntervalService,
			private $scope: LocalScope,
			private $timeout: ng.ITimeoutService,
			private $window: ng.IWindowService,
			private ChatService: ChatService,
			private ToastService: ToastService
		) {
			this.showChat = $scope.menu.showChat;
			this.ChatService.subscribeToUnread(this.receiveUnread.bind(this));

			$scope.$watch(
				() => $scope.menu.showChat,
				(newValue, oldValue) => {
					this.showChat = newValue;

					if (!newValue && oldValue) {
						this.closeChat();
						this.stopGettingUsers();
					} else if (newValue && !oldValue) {
						this.$timeout(() => {
							this.chatPanel = this.$document[0].getElementById('chat-slideout');
							this.chatInput = this.$document[0].getElementById('chat-input');

							if (this.chatInput) {
								this.chatInput.addEventListener('keypress', event => {
									if (event.which === this.ENTER_KEYCODE && !event.shiftKey && !event.ctrlKey) {
										this.sendMessage();
										event.preventDefault();
									}
								});
								this.chatInput.addEventListener('focus', event => {
									this.$document[0].getElementById('chat-header').classList.add('hidden-while-chatting');
									this.$document[0].querySelector('#chat-slideout .mdl-layout__header').classList.add('hidden-while-chatting');
								});
								this.chatInput.addEventListener('blur', event => {
									this.$document[0].getElementById('chat-header').classList.remove('hidden-while-chatting');
									this.$document[0].querySelector('#chat-slideout .mdl-layout__header').classList.remove('hidden-while-chatting');
								});
							}

							this.startGettingUsers();
						}, 150);
					}
				}
			);
		}

		openChat(user: ChatUser) {
			this.chatListener = this.$scope.$watch(
				() => this.currentChat.id,
				id => id === null && this.closeChat()
			);

			this.usersOpen = false;
			this.currentChat = user;
			this.currentMessageLog = this.messageLogs.find(log => {
				return log.id === this.currentChat.id;
			}) || {
				id: this.currentChat.id,
				log: []
			};
			this.currentDraft = this.drafts.find(draft => {
				return draft.recipient === this.currentChat.id;
			}) || {
				recipient: this.currentChat.id,
				message: ''
			};
			this.ChatService
				.subscribeToChat(
					this.currentChat.id,
					this.receiveMessage.bind(this)
				)
				.then(sub => {
					this.subscription = sub;
					this.getMessages();
					this.stopGettingUsers();
				})
				.catch(() => this.closeChat);
		};

		closeChat() {
			if (this.chatListener) {
				this.chatListener();
			}
			this.ChatService.unsubscribeFromChat(this.subscription)
				.then(() => this.subscription = null);
			this.usersOpen = true;
			var logIndex = this.messageLogs.findIndex(log => {
					return log.id === this.currentChat.id;
				}),
				draftIndex = this.drafts.findIndex(draft => {
					return draft.recipient === this.currentChat.id;
				});

			this.messageLogs[logIndex !== -1 ? logIndex : this.messageLogs.length] = this.currentMessageLog;
			this.currentMessageLog = {
				id: null,
				log: []
			};
			this.drafts[draftIndex !== -1 ? draftIndex : this.drafts.length] = this.currentDraft;
			this.currentDraft = {
				recipient: null,
				message: ''
			};
			this.currentChat.id = null;
			this.startGettingUsers();
		};

		getUsers() {
			this.ChatService.getUsers()
				.then(users => this.users = users)
				.finally(() => this.usersLoaded = true);
		};

		startGettingUsers() {
			if (!this.getUserInterval) {
				this.getUserInterval = this.$interval(this.getUsers, this.REFRESH_USERS_DELAY);
			}
			this.getUsers();
		};

		stopGettingUsers() {
			if (this.getUserInterval) {
				this.$interval.cancel(this.getUserInterval);
				this.getUserInterval = null;
			}
		};

		toggleGettingUsers() {
			if (this.chatPanel.classList.contains('active') && !this.currentChat.id) {
				this.startGettingUsers();
			} else {
				this.stopGettingUsers();
			}
		};

		sendMessage(retry: boolean = false, message = this.currentDraft.message) {
			if (!retry && (this.sending || message === '')) {
				return;
			}
			this.sending = true;

			this.ChatService
				.sendMessage(
					message,
					<number>this.currentDraft.recipient
				)
				.then(() => {
					this.currentDraft.message = '';
					this.sending = false;
					this.$timeout(() => {
						this.$document[0].getElementById('chat-input').focus();
					}, 10);
				});
		};

		formatMessageDate(index: number) {
			var msgDate = moment(this.currentMessageLog.log[index].timestamp);

			if (index !== 0 && msgDate.isBefore(moment(this.currentMessageLog.log[index - 1].timestamp).add(2, 'minutes'))) {
				return '';
			}

			return msgDate.format('M/DD/YY h:mm a');
		};

		format(message: string) {
			const elem = angular.element('<div></div>');

			elem.text(message);
			message = elem.html();

			return message.replace(
				// eslint-disable-next-line max-len
				/(https?:\/\/)?([0-9a-zA-Z_-]+\.)+[0-9a-zA-Z_-]+(:\d+)?(\/[0-9a-zA-Z_-]+)*\/?(\?([0-9a-zA-Z_=-]|(&amp;))*)?/,
				function(match, schema) {
					match = match.replace(/&amp;/, '&');
					return '<a href="' + (schema ? '' : 'https://') + match + '" target="_blank">' + match + '</a>';
				}
			);
		};

		isOwnMessage(message: Message) {
			return this.currentChat.id !== (<{id: number, name: string}>message.sender).id;
		}

		receiveUnread(count: number, users: number[]) {
			this.totalUnread = count;

			if (this.showChat) {
				if (this.currentChat.id && users.indexOf(this.currentChat.id) >= 0) {
					this.ChatService.markRead(this.currentChat.id);
					this.totalUnread -= 1;
					return;
				}

				this.getUsers();
			}

			if (this.totalUnread === 0) {
				Array.from(this.$document[0].getElementsByClassName('total-unread')).forEach((element: HTMLElement) => {
					element.style.display = 'none';
				});
			} else {
				Array.from(this.$document[0].getElementsByClassName('total-unread')).forEach((element: HTMLElement) => {
					element.innerHTML = this.totalUnread.toString();
					element.style.display = 'block';
				});
			}
		}

		receiveMessage(msg: Message[]) {
			const message = msg[0];

			if (!this.isOwnMessage(message)) {
				if (!message.read) {
					this.ChatService.markRead(this.currentChat.id);
				}
			}

			if (this.currentMessageLog.log.length && message.id <= this.currentMessageLog.log[this.currentMessageLog.log.length - 1].id) {
				// Update the current log's data
				var current = this.currentMessageLog.log.find(function(logMessage) {
					return logMessage.id === message.id;
				});

				if (current) {
					Object.keys(message).forEach(function(key) {
						if (message.hasOwnProperty(key)) {
							current[key] = message[key];
						}
					});
				}

				return;
			}

			this.currentMessageLog.log.push(message);
		};

		getOldMessages(): null|ng.IPromise<Message[]> {
			if (this.loadingOld || this.loading) {
				return null;
			}

			let date = null,
				target = this.currentChat.id;

			this.loadingOld = true;
			if (this.currentMessageLog.log.length > 0) {
				date = this.currentMessageLog.log[0].timestamp;
			}

			return this.ChatService
				.getOldMessages(target, date)
				.then(messages => {
					if (this.loading || !this.currentChat || this.currentChat.id !== target) {
						return [];
					}

					this.loadingOld = false;
					this.lastMessageLength = messages.length;
					if (messages && messages.length) {
						this.currentMessageLog.log = messages
							.filter(m => this.currentMessageLog.log.findIndex(m2 => m2.id === m.id) === -1)
							.concat(this.currentMessageLog.log);
					}
					this.retries = 0;

					return messages;
				})
				.catch(() => {
					if (this.loading || !this.currentChat || this.currentChat.id !== target) {
						return [];
					}

					this.retries++;
					if (this.retries <= this.MAX_RETRIES) {
						this.$timeout(this.getOldMessages, this.RETRY_DELAY);
						this.ToastService.show('Unable to retrieve messages. Retrying...')
					} else {
						this.retries = 0;
						this.loadingOld = false;
						this.ToastService.show('Unable to retrieve messages.')
					}

					return [];
				});
		}

		isLastOutbound(): boolean {
			return this.currentMessageLog.log && this.currentMessageLog.log.length
				&& this.isOwnMessage(this.currentMessageLog.log[this.currentMessageLog.log.length - 1]);
		}

		getMessages(): ng.IPromise<Message[]> {
			let lastRetrieved = null;

			this.loading = true;
			if (this.currentMessageLog.log.length > 0) {
				lastRetrieved = this.currentMessageLog.log[this.currentMessageLog.log.length - 1].timestamp;
			}

			return this.ChatService
				.getMessages(this.currentChat.id, lastRetrieved)
				.then(messages => {
					this.loading = false;
					if (messages && messages.length) {
						this.currentMessageLog.log = this.currentMessageLog.log.concat(
							messages.filter(m => this.currentMessageLog.log.findIndex(m2 => m2.id === m.id) === -1)
						);
					}
					this.retries = 0;

					return messages;
				})
				.catch(() => {
					this.retries++;
					if (this.retries <= this.MAX_RETRIES) {
						this.$timeout(this.getMessages, this.RETRY_DELAY);
						this.ToastService.show('Unable to retrieve messages. Retrying...')
					} else {
						this.retries = 0;
						this.loading = false;
						this.ToastService.show('Unable to retrieve messages.')
					}

					return [];
				});
		};

		searchUsers(user: ChatUser, index: number, users: ChatUser[]) {
			if (user.id === users[users.length - 1].id) {
				this.searchRegExp = new RegExp(this.search, 'i');
			}

			return !this.searchRegExp || this.searchRegExp.test(user.name);
		};
	}
}
